import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { uploadData } from "aws-amplify/storage";
import { v4 as uuid } from "uuid";
import { createArt } from "../../graphql/mutations";
import { useBanner } from "../CustomHooks/useBanner";
import "../Books/FileUpload.css";
import "./ArtGallery.css";

const client = generateClient();

const ArtUpload: React.FC = () => {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { bannerMessage, bannerVisible, isError, showBanner } = useBanner();
  const navigate = useNavigate();

  const handleUpload = async () => {
    if (!title.trim() || !artist.trim() || !imageFile) {
      showBanner("Please fill in title, artist, and select an image", true);
      return;
    }

    setIsUploading(true);

    try {
      const fileKey = `art/${uuid()}_${imageFile.name}`;

      await uploadData({
        key: fileKey,
        data: imageFile,
        options: {
          contentType: imageFile.type,
        },
      }).result;

      await client.graphql({
        query: createArt,
        variables: {
          input: {
            title: title.trim(),
            artist: artist.trim(),
            imageKey: fileKey,
          },
        },
      });

      showBanner("Artwork uploaded successfully!");
      setTitle("");
      setArtist("");
      setImageFile(null);
      setTimeout(() => navigate("/art-gallery"), 1500);
    } catch (error) {
      console.error("Error uploading art:", error);
      showBanner("Error uploading artwork. Please try again.", true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      setImageFile(event.target.files[0]);
    }
  };

  return (
    <div className="file-upload-container">
      <div className="file-upload-contents">
        <Link to="/art-gallery" className="back-button">
          ← Back to Art Gallery
        </Link>
        <h1>Upload Art</h1>
        {bannerVisible && (
          <div className={`banner ${isError ? "banner-error" : ""}`}>
            {bannerMessage}
          </div>
        )}
        <div className="file-upload-input">
          <div className="input-row">
            <label htmlFor="art-title">Title:</label>
            <input
              id="art-title"
              type="text"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="input-row">
            <label htmlFor="art-artist">Artist:</label>
            <input
              id="art-artist"
              type="text"
              placeholder="Artist"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              required
            />
          </div>
          <div className="input-row">
            <label htmlFor="art-image">Image:</label>
            <input
              id="art-image"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              required
            />
          </div>
        </div>
        <button
          className="upload-button"
          type="button"
          onClick={handleUpload}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </button>
      </div>
    </div>
  );
};

export default ArtUpload;
